import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { useNotification } from '../hooks/useNotification';
import {
  FiCalendar,
  FiClock,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiDownload
} from 'react-icons/fi';

const PaymentPlansPage = () => {
  const { showSuccess, showError } = useNotification();

  const [plans, setPlans] = useState([
    { id: 1, name: 'Day 5', frequency: 5, counter: 12 },
    { id: 2, name: 'Day 10', frequency: 10, counter: 8 },
    { id: 3, name: 'Day 15', frequency: 15, counter: 15 },
    { id: 4, name: 'Day 20', frequency: 20, counter: 6 },
    { id: 5, name: 'Day 25', frequency: 25, counter: 10 },
    { id: 6, name: 'Day 30', frequency: 30, counter: 20 },
    { id: 7, name: 'Day 45', frequency: 45, counter: 5 },
    { id: 8, name: 'Day 60', frequency: 60, counter: 3 },
    { id: 9, name: 'Day 90', frequency: 90, counter: 2 },
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanFrequency, setNewPlanFrequency] = useState('');
  const [newPlanCounter, setNewPlanCounter] = useState('');

  const totalPlans = plans.length;
  const activePlans = plans.filter(p => p.counter > 0).length;
  const totalCustomers = plans.reduce((sum, p) => sum + p.counter, 0);
  const avgFrequency = Math.round(plans.reduce((sum, p) => sum + p.frequency, 0) / plans.length);

  const handleAddPlan = () => {
    if (!newPlanName || !newPlanFrequency || !newPlanCounter) {
      showError('Please fill in all fields');
      return;
    }

    const newPlan = {
      id: plans.length + 1,
      name: newPlanName,
      frequency: parseInt(newPlanFrequency),
      counter: parseInt(newPlanCounter)
    };

    setPlans([...plans, newPlan]);
    showSuccess('Payment plan added successfully!');
    setShowAddModal(false);
    setNewPlanName('');
    setNewPlanFrequency('');
    setNewPlanCounter('');
  };

  const handleEditPlan = () => {
    if (!selectedPlan) return;

    const updatedPlans = plans.map(p =>
      p.id === selectedPlan.id
        ? { ...p, name: newPlanName, frequency: parseInt(newPlanFrequency), counter: parseInt(newPlanCounter) }
        : p
    );

    setPlans(updatedPlans);
    showSuccess('Payment plan updated successfully!');
    setShowEditModal(false);
    setSelectedPlan(null);
  };

  const handleDeletePlan = () => {
    if (!selectedPlan) return;

    setPlans(plans.filter(p => p.id !== selectedPlan.id));
    showSuccess('Payment plan deleted successfully!');
    setShowDeleteModal(false);
    setSelectedPlan(null);
  };

  const openEditModal = (plan) => {
    setSelectedPlan(plan);
    setNewPlanName(plan.name);
    setNewPlanFrequency(plan.frequency.toString());
    setNewPlanCounter(plan.counter.toString());
    setShowEditModal(true);
  };

  const openDeleteModal = (plan) => {
    setSelectedPlan(plan);
    setShowDeleteModal(true);
  };

  const handleExport = (format) => {
    showSuccess(`Exporting payment plans to ${format}...`);
  };

  return (
    <Layout title="Payment Plans">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={FiCalendar}
          value={totalPlans}
          label="Total Plans"
          bgColor="bg-pastel-lavender"
          iconColor="text-white"
          iconBgColor="bg-primary-500"
        />
        <StatCard
          icon={FiClock}
          value={activePlans}
          label="Active Plans"
          bgColor="bg-pastel-mint"
          iconColor="text-white"
          iconBgColor="bg-success-500"
        />
        <StatCard
          icon={FiClock}
          value={totalCustomers}
          label="Total Customers"
          bgColor="bg-pastel-peach"
          iconColor="text-white"
          iconBgColor="bg-warning-500"
        />
        <StatCard
          icon={FiCalendar}
          value={`${avgFrequency} Days`}
          label="Avg Frequency"
          bgColor="bg-pastel-pink"
          iconColor="text-white"
          iconBgColor="bg-danger-500"
        />
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex gap-4 items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">Frequency-Based Plans</h2>
          <p className="text-sm text-neutral-600">Manage repayment or lending frequency options</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddModal(true)}>
            <FiPlus className="w-4 h-4 mr-2 inline" />
            Add New Plan
          </Button>
          <div className="relative group">
            <Button variant="outline">
              <FiDownload className="w-4 h-4 mr-2 inline" />
              Export
            </Button>
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg border border-neutral-200 z-10 overflow-hidden hidden group-hover:block">
              <button
                onClick={() => handleExport('Excel')}
                className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Export to Excel
              </button>
              <button
                onClick={() => handleExport('PDF')}
                className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Export to PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className="bg-white rounded-card shadow-card p-6 hover:shadow-card-hover transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <FiCalendar className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">{plan.name}</h3>
                  <p className="text-sm text-neutral-600">Every {plan.frequency} days</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => openEditModal(plan)}
                  className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <FiEdit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => openDeleteModal(plan)}
                  className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">Frequency</span>
                <span className="text-sm font-semibold text-neutral-900">{plan.frequency} days</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">Active Customers</span>
                <span className="text-lg font-bold text-primary-600">{plan.counter}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-neutral-200">
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>Next payment in</span>
                <span className="font-medium">{plan.frequency} days</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Plan Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewPlanName('');
          setNewPlanFrequency('');
          setNewPlanCounter('');
        }}
        title="Add New Payment Plan"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Plan Name
            </label>
            <input
              type="text"
              value={newPlanName}
              onChange={(e) => setNewPlanName(e.target.value)}
              placeholder="e.g., Day 45"
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Frequency (Days)
            </label>
            <input
              type="number"
              value={newPlanFrequency}
              onChange={(e) => setNewPlanFrequency(e.target.value)}
              placeholder="e.g., 45"
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Initial Customer Count
            </label>
            <input
              type="number"
              value={newPlanCounter}
              onChange={(e) => setNewPlanCounter(e.target.value)}
              placeholder="e.g., 0"
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setNewPlanName('');
                setNewPlanFrequency('');
                setNewPlanCounter('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddPlan}>
              Add Plan
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Plan Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPlan(null);
        }}
        title="Edit Payment Plan"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Plan Name
            </label>
            <input
              type="text"
              value={newPlanName}
              onChange={(e) => setNewPlanName(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Frequency (Days)
            </label>
            <input
              type="number"
              value={newPlanFrequency}
              onChange={(e) => setNewPlanFrequency(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Customer Count
            </label>
            <input
              type="number"
              value={newPlanCounter}
              onChange={(e) => setNewPlanCounter(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setSelectedPlan(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleEditPlan}>
              Update Plan
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedPlan(null);
        }}
        title="Delete Payment Plan"
      >
        <div className="space-y-4">
          <p className="text-neutral-700">
            Are you sure you want to delete the payment plan "{selectedPlan?.name}"? This action cannot be undone.
          </p>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedPlan(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeletePlan}>
              Delete Plan
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default PaymentPlansPage;
